import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const SUPPORT_EMAIL = "support@startupotter.com";

function fmtDate(s) {
  if (!s) return null;
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return null; }
}

export function RestoreAccountDialog({ open, onOpenChange, email, purgeAt, restorable = true }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("choose"); // choose | code
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const until = fmtDate(purgeAt);

  const reset = () => { setStep("choose"); setSent(false); setCode(""); setBusy(false); };
  const close = () => { onOpenChange(false); reset(); };

  const sendCode = async () => {
    setBusy(true);
    try {
      await api.post("/auth/restore/request-code", { email });
      setSent(true);
      toast.success("Restore code sent to your email.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the code."); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post("/auth/restore", { email, code });
      toast.success("Your account has been restored. Please sign in again.");
      close();
    } catch (e) { toast.error(e?.response?.data?.detail || "That code is invalid or expired."); }
    finally { setBusy(false); }
  };

  // Admin-deleted (or otherwise non-restorable) accounts: no self-restore path.
  if (!restorable) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent data-testid="restore-account-dialog">
          <DialogHeader>
            <DialogTitle data-testid="restore-unavailable-title">This account is unavailable</DialogTitle>
            <DialogDescription>
              This account has been removed by an administrator and can’t be restored here.
              Please contact support if you believe this is a mistake.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate("/")} data-testid="restore-return-home">
              Return home
            </Button>
            <Button asChild data-testid="restore-contact-support">
              <a href={`mailto:${SUPPORT_EMAIL}`}>Contact support</a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="restore-account-dialog">
        <DialogHeader>
          <DialogTitle>Restore your account?</DialogTitle>
          <DialogDescription>
            This account is pending deletion{until ? ` until ${until}` : ""}. Restore it to cancel the
            deletion and keep your profile, handle and history. We’ll email a code to confirm it’s you.
          </DialogDescription>
        </DialogHeader>

        {step === "choose" && (
          <div className="space-y-2">
            <Button type="button" className="w-full" onClick={() => setStep("code")} data-testid="restore-choose-restore">
              Restore account
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={close} data-testid="restore-keep-deleted">
              Keep account deleted
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate("/")} data-testid="restore-return-home">
              Return home
            </Button>
            <p className="pt-1 text-center text-xs text-muted-foreground">
              Need help?{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline" data-testid="restore-contact-support">
                Contact support
              </a>
            </p>
          </div>
        )}

        {step === "code" && (
          !sent ? (
            <Button type="button" onClick={sendCode} disabled={busy} data-testid="restore-send-code">
              {busy ? "Sending…" : "Email me a restore code"}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Enter the 6-digit code sent to {email}</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                       maxLength={6} inputMode="numeric" data-testid="restore-code-input" />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="ghost" onClick={sendCode} disabled={busy} data-testid="restore-resend">Resend</Button>
                <Button type="button" onClick={restore} disabled={busy || code.trim().length !== 6} data-testid="restore-submit">
                  {busy ? "Restoring…" : "Restore account"}
                </Button>
              </DialogFooter>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
